// src/components/dashboard/employer/needs/NeedFormModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useEmployer } from '@/contexts/EmployerContext';
import {
    CreateNeedRequest, UpdateNeedRequest, Need, NeedContact,
    EmploymentType, EmploymentExtent, SalaryType,
    EmploymentTypeLabels, EmploymentExtentLabels,
} from '@/lib/models/need';
import { Module } from '@/lib/models/module';
import { moduleApi } from '@/lib/api/moduleApi';
import { prerequisiteApi } from '@/lib/api/prerequisiteApi';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { OnboardingProgram } from '@/lib/models/onboarding';
import { useAuth } from '@/contexts/AuthContext';
import { X, Briefcase, Users, Calendar, MapPin, Clock, Building2, BookOpen, Plus, Trash2, Wallet, Hash, CalendarClock, UserRound, GraduationCap, Pencil, UserPlus, FileText, AlertTriangle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import TagInput from '@/components/custom-ui/TagInput';
import { ActionButton, OutlineButton, ErrorState, IconChip, IslandSlideOver, MarkdownEditor, InlineSelect, LinkAssistPopover } from '@/components/custom-ui';
import NeedView, { NeedViewData } from '@/components/needs/NeedView';
import NeedSeedPanel from '@/components/dashboard/employer/needs/NeedSeedPanel';
import { GeneratedNeed, needApi } from '@/lib/api/needApi';
import { sourcesApi } from '@/lib/api/sourcesApi';

// ── Local draft persistence (create only) ───────────────────────────────
// A new annons is easy to lose (accidental close, reload). We keep an
// auto-saved draft in localStorage so it can be restored. Cleared on a
// successful create or an explicit discard.
const DRAFT_KEY = 'nexilink:need-draft:v1';
interface NeedDraft {
    formData: any;
    details: any;
    contacts: NeedContact[];
    selectedModuleIds: string[];
    programId: string;
}
const readDraft = (): NeedDraft | null => {
    try { const s = localStorage.getItem(DRAFT_KEY); return s ? JSON.parse(s) as NeedDraft : null; } catch { return null; }
};
const writeDraft = (d: NeedDraft) => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* quota / private mode — non-fatal */ }
};
const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* non-fatal */ }
};

// ── Shared field chrome (matches NeedView's card look) ──────────────────
const INPUT_CLS =
    'w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-shadow';

const FormSection: React.FC<{
    icon: LucideIcon;
    title: string;
    subtitle?: string;
    tone?: 'brand' | 'amber' | 'neutral';
    right?: React.ReactNode;
    children: React.ReactNode;
}> = ({ icon, title, subtitle, tone = 'brand', right, children }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-2.5 min-w-0">
                <IconChip icon={icon} size="sm" tone={tone} />
                <div className="min-w-0">
                    <h3 className="font-semibold text-sm text-gray-900">{title}</h3>
                    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
                </div>
            </div>
            {right}
        </div>
        {children}
    </div>
);

const FieldLabel: React.FC<{ children: React.ReactNode; icon?: LucideIcon }> = ({ children, icon: Icon }) => (
    <label className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-gray-400" />} {children}
    </label>
);

// Quiet inline input — borderless, transparent, with a bottom-border that
// appears on hover and strengthens on focus. This is the "modern" field look
// (same treatment as the role/unit header inputs) used across the whole form.
const TILE_INPUT =
    'w-full bg-transparent text-[15px] font-medium text-gray-900 border-0 border-b border-transparent hover:border-gray-300 focus:border-[#1A4D4F] focus:outline-none focus:ring-0 px-0 py-1 placeholder:text-gray-300 placeholder:font-normal transition-colors';

// Same quiet treatment for contact rows (on white cards).
const QUIET_INPUT = TILE_INPUT;

// A stat-tile-shaped field: mirrors NeedView's read Tile (uppercase label + icon)
// but hosts editable control(s). Keeps the edit surface looking like the detail
// page. NOTE: defined at module scope (not inside the component) so its inputs
// don't remount on every render and steal focus while typing.
const FieldTile: React.FC<{
    icon: LucideIcon;
    label: string;
    children: React.ReactNode;
    className?: string;
}> = ({ icon: Icon, label, children, className }) => (
    <div className={`p-3.5 rounded-xl bg-gray-50 border border-gray-100 ${className || ''}`}>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5 mb-1.5">
            <Icon className="w-4 h-4 text-[#1A4D4F]" /> {label}
        </p>
        {children}
    </div>
);

// Generic people-oriented competencies
const COMPETENCY_SUGGESTIONS = [
    'Kommunikation',
    'Problemlösning',
    'Teamwork',
    'Ledarskap',
    'Anpassningsförmåga',
    'Kreativitet',
    'Kritiskt tänkande',
    'Beslutsfattande',
    'Tidsplanering',
    'Analytiskt tänkande',
    'Kundservice',
    'Konflikthantering',
    'Initiativförmåga',
    'Självständighet',
    'Samarbetsförmåga',
    'Empati',
    'Flexibilitet',
    'Noggrannhet',
    'Stresshantering',
    'Dokumentation'
];

export type NeedFormMode = 'create' | 'edit' | 'duplicate';

interface NeedFormModalProps {
    mode: NeedFormMode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    existingNeed?: Need | null;
    onSuccess?: (need?: Need) => void;
}

const NeedFormModal: React.FC<NeedFormModalProps> = ({
                                                         mode,
                                                         open,
                                                         onOpenChange,
                                                         existingNeed,
                                                         onSuccess
                                                     }) => {
    const { createNeed, updateNeed, isLoadingNeeds, needError, clearNeedError, employerDetails } = useEmployer();
    const { user } = useAuth();

    const getTodayDate = () => new Date().toISOString().split('T')[0];
    const unitDefaults = employerDetails?.unit;

    // Build a contact from the signed-in employer's own details (whatever we have).
    const selfContact = (): NeedContact => {
        const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
            || employerDetails?.user?.full_name || '';
        return {
            name: fullName,
            role: employerDetails?.employer?.job_title || '',
            email: user?.email || employerDetails?.user?.email || '',
            phone: user?.phone_number || unitDefaults?.contact_phone || '',
        };
    };

    const [formData, setFormData] = useState({
        title: '',
        role: '',
        description: '',
        competencies: [] as string[],
        min_hours_per_week: 20,
        max_hours_per_week: 30,
        start_date: getTodayDate(),
        city: unitDefaults?.city || '',
        work_location_on_site: true,
        work_location_remote: false,
        offers_supervision: false,
    });

    // Job-posting details (#217). Kept separate from formData (which is spread
    // straight into the request) because numeric fields live as strings here so
    // they can be left blank; they're parsed/merged explicitly in submit.
    const emptyDetails = {
        application_deadline: '',
        employment_type: 'permanent' as EmploymentType,
        employment_extent: 'full_time' as EmploymentExtent,
        extent_percent: '',
        positions_count: '1',
        salary_type: 'by_agreement' as SalaryType,
        salary_min: '',
        salary_max: '',
    };
    const [details, setDetails] = useState(emptyDetails);
    const [contacts, setContacts] = useState<NeedContact[]>([]);

    // Redigera ↔ Förhandsgranska (preview renders the SAME NeedView candidates see).
    const [view, setView] = useState<'edit' | 'preview'>('edit');

    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // Unsaved-changes guard + local draft (create) state.
    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [draftRestored, setDraftRestored] = useState(false);
    // Baseline snapshot of the synchronously-known editable fields, captured after
    // (re)init; dirtiness = current fields differ from it. programId is known
    // synchronously (from existingNeed / the draft) so it lives here too.
    const baselineRef = useRef<string | null>(null);
    const coreSnapshot = () => JSON.stringify({ formData, details, contacts, programId });
    // The required prerequisite courses load async (getRequiredModules resolves
    // after open), so they get their OWN baseline captured once that load settles —
    // otherwise the async populate would read as an instant edit. Folded into
    // isDirty so adding/removing a prerequisite course enables "Spara ändringar".
    const coursesBaselineRef = useRef<string | null>(null);
    const coursesSnapshot = () => JSON.stringify([...selectedModuleIds].sort());

    // Course-based application prerequisites (#217)
    const [availableModules, setAvailableModules] = useState<Module[]>([]);
    const [selectedModuleIds, setSelectedModuleIds] = useState<string[]>([]);

    // Role-specific onboarding program (assigned to whoever is hired into this posting).
    const [availablePrograms, setAvailablePrograms] = useState<OnboardingProgram[]>([]);
    const [programId, setProgramId] = useState<string>('');

    // Load available cases and existing prerequisites when modal opens
    useEffect(() => {
        if (!open) return;

        // Always reset when opening — clear any stale validation error from a
        // previous attempt so it doesn't pre-populate the fresh form.
        setValidationErrors([]);
        setView('edit');
        clearNeedError();
        setSelectedModuleIds([]);
        // Re-arm the courses baseline each open; it's re-set once the async load
        // (edit) or the create branch below settles, so the null-guard in isDirty
        // suppresses any false "dirty" during the load window.
        coursesBaselineRef.current = null;
        setProgramId(existingNeed && mode !== 'create' ? (existingNeed.onboarding_program_id || '') : '');

        moduleApi.getMyModules().then(modules => setAvailableModules(modules || [])).catch(() => {});
        onboardingApi.getPrograms().then(res => setAvailablePrograms(res.programs || [])).catch(() => {});

        // Load existing required courses when editing. Capture the courses baseline
        // from the loaded set so a later add/remove registers as a real change.
        if (mode === 'edit' && existingNeed) {
            prerequisiteApi.getRequiredModules(existingNeed.id).then(reqs => {
                const ids = [...new Set(reqs.map(r => r.module_id))];
                setSelectedModuleIds(ids);
                coursesBaselineRef.current = JSON.stringify([...ids].sort());
            }).catch(() => {
                coursesBaselineRef.current = JSON.stringify([]);
            });
        }
    }, [open, mode, existingNeed?.id]);

    useEffect(() => {
        if (open && existingNeed && (mode === 'edit' || mode === 'duplicate')) {
            let existingNeedCompetencies = existingNeed.competencies || [];
            if (!Array.isArray(existingNeedCompetencies)) {
                existingNeedCompetencies = [];
            }

            const fd = {
                title: mode === 'duplicate' ? `${existingNeed.title} (Kopia)` : existingNeed.title,
                role: existingNeed.role,
                description: existingNeed.description || '',
                competencies: existingNeedCompetencies,
                min_hours_per_week: existingNeed.min_hours_per_week,
                max_hours_per_week: existingNeed.max_hours_per_week,
                start_date: mode === 'duplicate' ? getTodayDate() : existingNeed.start_date,
                city: existingNeed.city,
                work_location_on_site: existingNeed.work_location_on_site,
                work_location_remote: existingNeed.work_location_remote,
                offers_supervision: existingNeed.offers_supervision,
            };
            const dt = {
                application_deadline: existingNeed.application_deadline || '',
                employment_type: existingNeed.employment_type || 'permanent',
                employment_extent: existingNeed.employment_extent || 'full_time',
                extent_percent: existingNeed.extent_percent != null ? String(existingNeed.extent_percent) : '',
                positions_count: existingNeed.positions_count != null ? String(existingNeed.positions_count) : '1',
                salary_type: existingNeed.salary_type || 'by_agreement',
                salary_min: existingNeed.salary_min != null ? String(existingNeed.salary_min) : '',
                salary_max: existingNeed.salary_max != null ? String(existingNeed.salary_max) : '',
            };
            const ct = Array.isArray(existingNeed.contacts) ? existingNeed.contacts : [];
            setFormData(fd);
            setDetails(dt as typeof emptyDetails);
            setContacts(ct);
            setDraftRestored(false);
            baselineRef.current = JSON.stringify({ formData: fd, details: dt, contacts: ct, programId: existingNeed.onboarding_program_id || '' });
        } else if (open && mode === 'create') {
            const draft = readDraft();
            if (draft && draft.formData) {
                setFormData(draft.formData);
                setDetails(draft.details);
                setContacts(draft.contacts || []);
                setSelectedModuleIds(draft.selectedModuleIds || []);
                setProgramId(draft.programId || '');
                setDraftRestored(true);
                baselineRef.current = JSON.stringify({ formData: draft.formData, details: draft.details, contacts: draft.contacts || [], programId: draft.programId || '' });
                coursesBaselineRef.current = JSON.stringify([...(draft.selectedModuleIds || [])].sort());
            } else {
                const fd = {
                    title: unitDefaults?.name || '',
                    role: '',
                    description: '',
                    competencies: [] as string[],
                    min_hours_per_week: 20,
                    max_hours_per_week: 30,
                    start_date: getTodayDate(),
                    city: unitDefaults?.city || '',
                    work_location_on_site: true,
                    work_location_remote: false,
                    offers_supervision: false,
                };
                setFormData(fd);
                setDetails(emptyDetails);
                setContacts([]);
                setDraftRestored(false);
                baselineRef.current = JSON.stringify({ formData: fd, details: emptyDetails, contacts: [], programId: '' });
                coursesBaselineRef.current = JSON.stringify([]);
            }
        }
    }, [open, mode, existingNeed, unitDefaults]);

    // Auto-save the create draft (debounced) whenever the form has content.
    useEffect(() => {
        if (!open || mode !== 'create') return;
        const hasContent = !!formData.role.trim() || !!formData.description.trim()
            || formData.competencies.length > 0 || contacts.length > 0;
        if (!hasContent) return;
        const t = setTimeout(() => {
            writeDraft({ formData, details, contacts, selectedModuleIds, programId });
        }, 600);
        return () => clearTimeout(t);
    }, [open, mode, formData, details, contacts, selectedModuleIds, programId]);

    const validateForm = (): boolean => {
        const errors: string[] = [];

        if (!formData.title.trim()) errors.push('Enhet är obligatorisk');
        if (!formData.role.trim()) errors.push('Rolltitel är obligatorisk');
        if (!formData.city.trim()) errors.push('Stad är obligatorisk');
        if (!formData.start_date) errors.push('Startdatum är obligatoriskt');
        if (!formData.competencies || formData.competencies.length === 0) {
            errors.push('Minst en kompetens måste väljas');
        }
        if (formData.min_hours_per_week > formData.max_hours_per_week) {
            errors.push('Minimum timmar kan inte vara högre än maximum timmar');
        }

        setValidationErrors(errors);
        return errors.length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        void doSubmit();
    };

    const doSubmit = async (): Promise<boolean> => {
        if (!validateForm()) {
            return false;
        }

        // Parse the #217 detail fields once (strings → numbers, drop blanks).
        const num = (v: string): number | undefined => {
            const n = parseInt(v, 10);
            return v.trim() !== '' && !isNaN(n) ? n : undefined;
        };
        const detailFields = {
            application_deadline: details.application_deadline || undefined,
            employment_type: details.employment_type,
            employment_extent: details.employment_extent,
            extent_percent: details.employment_extent === 'part_time' ? num(details.extent_percent) : undefined,
            positions_count: num(details.positions_count) ?? 1,
            contacts: contacts.filter(c => c.name.trim()),
            salary_type: details.salary_type,
            salary_min: details.salary_type === 'by_agreement' ? undefined : num(details.salary_min),
            salary_max: details.salary_type === 'by_agreement' ? undefined : num(details.salary_max),
        };

        try {
            if (mode === 'edit' && existingNeed) {
                const updateData: UpdateNeedRequest = {
                    ...formData,
                    ...detailFields,
                    onboarding_program_id: programId, // "" clears, id sets
                };
                const updatedNeed = await updateNeed(existingNeed.id, updateData);

                // Save required courses
                await prerequisiteApi.setRequiredModules(existingNeed.id, {
                    required_modules: selectedModuleIds.map(module_id => ({ module_id })),
                });

                baselineRef.current = coreSnapshot();
                onSuccess?.(updatedNeed);
                onOpenChange(false);
                return true;
            } else {
                const createData: CreateNeedRequest = {
                    ...formData,
                    ...detailFields,
                    onboarding_program_id: programId || undefined, // omit when none (UUID column)
                };
                const newNeed = await createNeed(createData);

                // Save required courses for the new need
                if (newNeed && selectedModuleIds.length > 0) {
                    await prerequisiteApi.setRequiredModules(newNeed.id, {
                        required_modules: selectedModuleIds.map(module_id => ({ module_id })),
                    });
                }

                clearDraft(); // created — the draft is now redundant
                baselineRef.current = coreSnapshot();
                // #320 — createNeed resolves `void | Need` (it returns nothing on a
                // handled failure), and the guarded branch above already treats a
                // falsy result as "no need". Only notify when one actually exists;
                // the old call could hand the parent undefined as a created Need.
                if (newNeed) onSuccess?.(newNeed);
                onOpenChange(false);
                return true;
            }
        } catch (err) {
            console.error('Failed to save need:', err);
            return false;
        }
        return false;
    };

    // ── Unsaved-changes guard ────────────────────────────────────────────
    const isDirty = () => open && baselineRef.current !== null && (
        baselineRef.current !== coreSnapshot() ||
        // Only compare courses once their async baseline has settled, so the
        // initial load doesn't count as an edit.
        (coursesBaselineRef.current !== null && coursesBaselineRef.current !== coursesSnapshot())
    );

    // Close request routed through the guard: prompt if there are unsaved edits.
    const requestClose = () => {
        if (isDirty()) setShowExitConfirm(true);
        else onOpenChange(false);
    };

    const discardAndClose = () => {
        setShowExitConfirm(false);
        onOpenChange(false);
    };

    const saveThenClose = async () => {
        const ok = await doSubmit();
        if (ok) setShowExitConfirm(false);
        // if validation failed, keep the modal open so the user can fix it
        else setShowExitConfirm(false);
    };

    const discardDraft = () => {
        clearDraft();
        setDraftRestored(false);
        // reset the create form to unit defaults
        setFormData({
            title: unitDefaults?.name || '', role: '', description: '', competencies: [],
            min_hours_per_week: 20, max_hours_per_week: 30, start_date: getTodayDate(),
            city: unitDefaults?.city || '', work_location_on_site: true,
            work_location_remote: false, offers_supervision: false,
        });
        setDetails(emptyDetails);
        setContacts([]);
        setSelectedModuleIds([]);
        setProgramId('');
        baselineRef.current = JSON.stringify({
            formData: { title: unitDefaults?.name || '', role: '', description: '', competencies: [], min_hours_per_week: 20, max_hours_per_week: 30, start_date: getTodayDate(), city: unitDefaults?.city || '', work_location_on_site: true, work_location_remote: false, offers_supervision: false },
            details: emptyDetails, contacts: [],
        });
    };

    const getTitle = () => {
        switch (mode) {
            case 'create':
                return 'Skapa annons';
            case 'edit':
                return 'Redigera annons';
            case 'duplicate':
                return 'Duplicera annons';
        }
    };

    // Live preview adapter — the same NeedViewData candidates' detail renders from.
    const formToView = (): NeedViewData => ({
        role: formData.role,
        unitLabel: formData.title,
        description: formData.description,
        competencies: formData.competencies,
        city: formData.city,
        minHours: formData.min_hours_per_week,
        maxHours: formData.max_hours_per_week,
        workLocationOnSite: formData.work_location_on_site,
        workLocationRemote: formData.work_location_remote,
        offersSupervision: formData.offers_supervision,
        startDate: formData.start_date,
        applicationDeadline: details.application_deadline || undefined,
        employmentType: details.employment_type,
        employmentExtent: details.employment_extent,
        extentPercent: details.extent_percent ? parseInt(details.extent_percent, 10) : undefined,
        positionsCount: details.positions_count ? parseInt(details.positions_count, 10) : undefined,
        contacts: contacts.filter((c) => c.name.trim()),
        salaryMin: details.salary_min ? parseInt(details.salary_min, 10) : undefined,
        salaryMax: details.salary_max ? parseInt(details.salary_max, 10) : undefined,
        salaryType: details.salary_type,
    });

    // Load a Link-generated draft into the editable form state.
    const loadGenerated = (g: GeneratedNeed) => {
        setFormData((prev) => ({
            ...prev,
            role: g.role || prev.role,
            description: g.description || '',
            competencies: g.competencies?.length ? g.competencies : prev.competencies,
            city: g.city || prev.city,
            start_date: g.start_date || prev.start_date,
            min_hours_per_week: g.min_hours_per_week || prev.min_hours_per_week,
            max_hours_per_week: g.max_hours_per_week || prev.max_hours_per_week,
            work_location_on_site: g.work_location_on_site,
            work_location_remote: g.work_location_remote,
            offers_supervision: g.offers_supervision,
        }));
        setDetails((prev) => ({
            ...prev,
            application_deadline: g.application_deadline || '',
            employment_type: (g.employment_type as EmploymentType) || prev.employment_type,
            employment_extent: (g.employment_extent as EmploymentExtent) || prev.employment_extent,
            extent_percent: g.extent_percent != null ? String(g.extent_percent) : '',
            positions_count: g.positions_count != null ? String(g.positions_count) : '1',
            salary_type: (g.salary_type as SalaryType) || prev.salary_type,
            salary_min: g.salary_min != null ? String(g.salary_min) : '',
            salary_max: g.salary_max != null ? String(g.salary_max) : '',
        }));
        setValidationErrors([]);
        clearNeedError();
    };

    const canvasHasContent = !!formData.role.trim() || !!formData.description.trim() || formData.competencies.length > 0;
    const showSeed = mode !== 'edit';

    const selectedCourseNames = selectedModuleIds
        .map((mid) => availableModules.find((m) => m.id === mid)?.module_name)
        .filter(Boolean) as string[];
    const selectedProgramName = availablePrograms.find((p) => p.id === programId)?.name;

    if (!open) return null;

    const hoursOnChange = (key: 'min_hours_per_week' | 'max_hours_per_week', v: number) => {
        if (Number.isNaN(v)) return;
        setFormData(key === 'min_hours_per_week'
            ? { ...formData, min_hours_per_week: v, max_hours_per_week: Math.max(v, formData.max_hours_per_week) }
            : { ...formData, max_hours_per_week: v, min_hours_per_week: Math.min(v, formData.min_hours_per_week) });
    };

    // The edit surface mirrors the read detail (NeedView): centered full-bleed
    // modal, inline-edit title, a stat-tile field grid, TipTap body, then the
    // same sections in the same order — but editable. (#223 Step 3a.)
    return (
        <IslandSlideOver onClose={requestClose} fullScreen zClass="z-50">
            {/* Header bar */}
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Pencil className="w-4 h-4 text-[#1A4D4F]" /> {getTitle()}
                </div>
                <button onClick={requestClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Stäng">
                    <X className="w-5 h-5 text-gray-500" />
                </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col bg-gray-50/40">
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    <div className={`mx-auto flex flex-col lg:flex-row gap-6 ${showSeed ? 'max-w-6xl' : 'max-w-3xl'}`}>
                        {/* Link side-rail — sits on top on small screens, becomes the
                            right column on desktop (order-last). */}
                        {showSeed && (
                            <aside className="order-first lg:order-last w-full lg:w-80 xl:w-96 flex-shrink-0 lg:self-start lg:sticky lg:top-0">
                                <NeedSeedPanel
                                    onSeeded={loadGenerated}
                                    hasContent={canvasHasContent}
                                    defaultOpen={mode === 'create' && !canvasHasContent}
                                />
                            </aside>
                        )}

                        {/* Main form column */}
                        <div className="flex-1 min-w-0 space-y-6">
                        {/* Error */}
                        {(needError || validationErrors.length > 0) && (
                            <ErrorState message={needError || validationErrors.join(', ')} onRetry={() => setValidationErrors([])} />
                        )}

                        {/* Restored-draft notice (create) */}
                        {draftRestored && (
                            <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                                <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Ett sparat utkast återställdes.</span>
                                <button type="button" onClick={discardDraft} className="font-medium text-amber-900 hover:underline flex-shrink-0">Rensa utkast</button>
                            </div>
                        )}

                        {/* Header — mirrors NeedView (icon + role + unit), inline-editable */}
                        <div className="flex items-start gap-3">
                            <IconChip icon={Briefcase} tone="brand" size="lg" />
                            <div className="flex-1 min-w-0">
                                <input
                                    type="text"
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                    placeholder="Rolltitel — t.ex. Socialsekreterare"
                                    className="w-full text-2xl font-bold text-gray-900 bg-transparent border-b border-transparent hover:border-gray-200 focus:border-[#1A4D4F] focus:outline-none placeholder:text-gray-300 placeholder:font-normal"
                                />
                                <div className="flex items-center gap-1.5 mt-1">
                                    <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        placeholder="Enhet — t.ex. Barn & Unga"
                                        className="flex-1 min-w-0 text-sm text-gray-500 bg-transparent border-b border-transparent hover:border-gray-200 focus:border-[#1A4D4F] focus:outline-none placeholder:text-gray-300"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Field grid — mirrors NeedView's stat tiles, as inputs */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                            <FieldTile icon={MapPin} label="Ort">
                                <input
                                    type="text"
                                    value={formData.city}
                                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                    placeholder="T.ex. Stockholm"
                                    className={TILE_INPUT}
                                />
                            </FieldTile>

                            <FieldTile icon={Clock} label="Omfattning (tim/v)">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number" min={1} max={40}
                                        value={formData.min_hours_per_week}
                                        onChange={(e) => hoursOnChange('min_hours_per_week', parseInt(e.target.value, 10))}
                                        className={`${TILE_INPUT} w-16 text-center`}
                                    />
                                    <span className="text-gray-400">–</span>
                                    <input
                                        type="number" min={1} max={40}
                                        value={formData.max_hours_per_week}
                                        onChange={(e) => hoursOnChange('max_hours_per_week', parseInt(e.target.value, 10))}
                                        className={`${TILE_INPUT} w-16 text-center`}
                                    />
                                </div>
                            </FieldTile>

                            <FieldTile icon={Briefcase} label="Arbetsform">
                                <div className="flex gap-1.5">
                                    {([['work_location_on_site', 'På plats'], ['work_location_remote', 'Distans']] as const).map(([key, label]) => {
                                        const active = formData[key];
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => setFormData({ ...formData, [key]: !active })}
                                                className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                                    active ? 'border-[#1A4D4F] bg-[#A4C3B2]/25 text-[#1A4D4F]' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                                                }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </FieldTile>

                            <FieldTile icon={Calendar} label="Startdatum">
                                <input
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                    className={TILE_INPUT}
                                />
                            </FieldTile>

                            <FieldTile icon={CalendarClock} label="Sista ansökningsdag">
                                <input
                                    type="date"
                                    value={details.application_deadline}
                                    onChange={(e) => setDetails({ ...details, application_deadline: e.target.value })}
                                    className={TILE_INPUT}
                                />
                            </FieldTile>

                            <FieldTile icon={Hash} label="Antal tjänster">
                                <input
                                    type="number" min={1}
                                    value={details.positions_count}
                                    onChange={(e) => setDetails({ ...details, positions_count: e.target.value })}
                                    className={TILE_INPUT}
                                />
                            </FieldTile>

                            <FieldTile icon={UserRound} label="Anställning" className="md:col-span-3">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <InlineSelect
                                        ariaLabel="Anställningsform"
                                        value={details.employment_type}
                                        onChange={(v) => setDetails({ ...details, employment_type: v as EmploymentType })}
                                        options={(Object.keys(EmploymentTypeLabels) as EmploymentType[]).map(t => ({ value: t, label: EmploymentTypeLabels[t] }))}
                                    />
                                    <InlineSelect
                                        ariaLabel="Omfattning"
                                        value={details.employment_extent}
                                        onChange={(v) => setDetails({ ...details, employment_extent: v as EmploymentExtent })}
                                        options={(Object.keys(EmploymentExtentLabels) as EmploymentExtent[]).map(x => ({ value: x, label: EmploymentExtentLabels[x] }))}
                                    />
                                    {details.employment_extent === 'part_time' && (
                                        <input
                                            type="number" min={1} max={100}
                                            value={details.extent_percent}
                                            onChange={(e) => setDetails({ ...details, extent_percent: e.target.value })}
                                            placeholder="Sysselsättningsgrad %"
                                            className={TILE_INPUT}
                                        />
                                    )}
                                </div>
                            </FieldTile>

                            <FieldTile icon={Wallet} label="Lön" className="md:col-span-3">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                    <InlineSelect
                                        ariaLabel="Lönetyp"
                                        className="sm:w-56"
                                        value={details.salary_type}
                                        onChange={(v) => setDetails({ ...details, salary_type: v as SalaryType })}
                                        options={[
                                            { value: 'by_agreement', label: 'Enligt överenskommelse' },
                                            { value: 'monthly', label: 'Månadslön' },
                                            { value: 'hourly', label: 'Timlön' },
                                        ]}
                                    />
                                    {details.salary_type !== 'by_agreement' && (
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number" min={0}
                                                value={details.salary_min}
                                                onChange={(e) => setDetails({ ...details, salary_min: e.target.value })}
                                                placeholder="Från"
                                                className={`${TILE_INPUT} w-28`}
                                            />
                                            <span className="text-gray-400">–</span>
                                            <input
                                                type="number" min={0}
                                                value={details.salary_max}
                                                onChange={(e) => setDetails({ ...details, salary_max: e.target.value })}
                                                placeholder="Till"
                                                className={`${TILE_INPUT} w-28`}
                                            />
                                            <span className="text-xs text-gray-500 whitespace-nowrap">{details.salary_type === 'hourly' ? 'kr/tim' : 'kr/mån'}</span>
                                        </div>
                                    )}
                                </div>
                            </FieldTile>
                        </div>

                        {/* Handledning — mirrors NeedView's green banner */}
                        <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl bg-green-50/60 border border-green-200">
                            <span className="text-sm text-green-900 flex items-center gap-2"><Users className="w-4 h-4" /> Handledning</span>
                            <div className="flex gap-1.5">
                                {([[true, 'Erbjuds'], [false, 'Krävs självständighet']] as const).map(([val, label]) => (
                                    <button
                                        key={String(val)}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, offers_supervision: val })}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                            formData.offers_supervision === val ? 'border-[#1A4D4F] bg-white text-[#1A4D4F]' : 'border-transparent text-gray-500 hover:bg-white/60'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Beskrivning — TipTap (mirrors the rendered body) */}
                        <section>
                            <h3 className="text-base font-semibold text-gray-900 mb-2">Beskrivning</h3>
                            <MarkdownEditor
                                value={formData.description}
                                onChange={(md) => setFormData({ ...formData, description: md })}
                                placeholder="Beskriv rollen och verksamheten — t.ex. Roll och uppdrag, Vi söker dig som, Vi erbjuder…"
                                minHeightClass="min-h-[220px]"
                                onImageUpload={async (file) => {
                                    const src = await sourcesApi.uploadSource(file);
                                    return sourcesApi.getPublicImageUrl(src.id);
                                }}
                                selectionAssistRun={(action, instruction, selectedText) => needApi.aiAssistNeed({
                                    action: action as any,
                                    content: selectedText,
                                    instruction,
                                    role: formData.role,
                                    scope: 'selection',
                                })}
                                toolbarExtra={
                                    <LinkAssistPopover
                                        subjectText={formData.description}
                                        run={(action, instruction) => needApi.aiAssistNeed({
                                            action: action as any,
                                            content: formData.description,
                                            instruction,
                                            role: formData.role,
                                        })}
                                        onApply={(revised) => setFormData((prev) => ({ ...prev, description: revised }))}
                                    />
                                }
                            />
                        </section>

                        {/* Kompetenser — mirrors the pills */}
                        <section>
                            <h3 className="text-base font-semibold text-gray-900 mb-2">Kompetenser</h3>
                            <TagInput
                                value={formData.competencies}
                                onChange={(tags) => setFormData({ ...formData, competencies: tags })}
                                suggestions={COMPETENCY_SUGGESTIONS}
                                placeholder="Välj eller lägg till kompetenser..."
                                suggestionsLabel="Förslag"
                                createNewLabel="Lägg till egen"
                                createNewPrefix="Skapa"
                            />
                        </section>

                        {/* Förkunskapsmoduler (course-based gating, #217) */}
                        {availableModules.length > 0 && (
                            <section>
                                <h3 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#1A4D4F]" /> Förkunskapsmoduler</h3>
                                <p className="text-xs text-gray-400 mb-2">Valfritt — kräv genomförda moduler innan ansökan.</p>
                                {selectedModuleIds.length > 0 && (
                                    <div className="space-y-2 mb-3">
                                        {selectedModuleIds.map(moduleId => {
                                            const m = availableModules.find(am => am.id === moduleId);
                                            return (
                                                <div key={moduleId} className="flex items-center justify-between p-2 bg-gray-50 border border-gray-100 rounded-lg">
                                                    <span className="text-sm text-gray-900">{m?.module_name || moduleId}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedModuleIds(prev => prev.filter(id => id !== moduleId))}
                                                        className="p-1 text-red-500 hover:bg-red-100 rounded"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <InlineSelect
                                    variant="outline"
                                    ariaLabel="Lägg till förkunskapsmodul"
                                    placeholder="+ Lägg till modul som förkunskapskrav…"
                                    value=""
                                    onChange={(val) => { if (val) setSelectedModuleIds(prev => prev.includes(val) ? prev : [...prev, val]); }}
                                    options={availableModules
                                        // #75 — only PUBLISHED courses can be newly added as a job
                                        // prerequisite. Drafts would leak to candidates via the
                                        // annons; archived courses are retired. The backend enforces
                                        // the same rule for newly-added courses.
                                        .filter(m => (m.status ?? 'draft') === 'published')
                                        .filter(m => !selectedModuleIds.includes(m.id))
                                        .map(m => ({ value: m.id, label: m.module_name }))}
                                />
                            </section>
                        )}

                        {/* Onboarding för rollen (assigned on hire) */}
                        {availablePrograms.length > 0 && (
                            <section>
                                <h3 className="text-base font-semibold text-gray-900 mb-1 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[#1A4D4F]" /> Onboarding för rollen</h3>
                                <p className="text-xs text-gray-400 mb-2">Valfritt — tilldelas automatiskt vid anställning.</p>
                                <InlineSelect
                                    variant="outline"
                                    ariaLabel="Onboarding för rollen"
                                    value={programId}
                                    onChange={(v) => setProgramId(v)}
                                    options={[
                                        { value: '', label: 'Enhetens standardprogram' },
                                        ...availablePrograms.map(p => ({ value: p.id, label: p.name })),
                                    ]}
                                />
                            </section>
                        )}

                        {/* Kontaktpersoner — mirrors the detail's Kontakt section */}
                        <section>
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2"><UserRound className="w-4 h-4 text-[#1A4D4F]" /> Kontaktpersoner</h3>
                                <div className="flex items-center gap-2">
                                    {selfContact().name && (
                                        <button
                                            type="button"
                                            onClick={() => setContacts([...contacts, selfContact()])}
                                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 flex-shrink-0"
                                        >
                                            <UserPlus className="w-3.5 h-3.5" /> Använd mina uppgifter
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setContacts([...contacts, { name: '', role: '', email: '', phone: '' }])}
                                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-[#1A4D4F]/20 text-[#1A4D4F] hover:bg-[#1A4D4F]/5 flex-shrink-0"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Lägg till
                                    </button>
                                </div>
                            </div>
                            {contacts.length === 0 ? (
                                <p className="text-xs text-gray-400 italic">Inga kontaktpersoner tillagda.</p>
                            ) : (
                                <div className="space-y-3">
                                    {contacts.map((c, i) => (
                                        <div key={i} className="relative p-3 pr-9 bg-white border border-gray-200 rounded-xl grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                                            <input
                                                type="text" value={c.name}
                                                onChange={(e) => setContacts(contacts.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                                                placeholder="Namn"
                                                className={QUIET_INPUT}
                                            />
                                            <input
                                                type="text" value={c.role || ''}
                                                onChange={(e) => setContacts(contacts.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}
                                                placeholder="Roll"
                                                className={QUIET_INPUT}
                                            />
                                            <input
                                                type="email" value={c.email || ''}
                                                onChange={(e) => setContacts(contacts.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
                                                placeholder="E-post"
                                                className={QUIET_INPUT}
                                            />
                                            <input
                                                type="tel" value={c.phone || ''}
                                                onChange={(e) => setContacts(contacts.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))}
                                                placeholder="Telefon"
                                                className={QUIET_INPUT}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setContacts(contacts.filter((_, j) => j !== i))}
                                                className="absolute top-2 right-2 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg"
                                                aria-label="Ta bort kontaktperson"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
                    <div className="flex gap-3 ml-auto">
                        {/* type="button" — without it this sits inside the <form> and
                            defaults to submit, so Avbryt would save + close (and flash
                            the unsaved-changes guard) instead of just requesting close. */}
                        <OutlineButton type="button" onClick={requestClose}>Avbryt</OutlineButton>
                        {/* Disable Save in edit mode until something changed — clearer
                            UX + a client-side guard mirroring the module editor. */}
                        <ActionButton type="submit" loading={isLoadingNeeds} disabled={mode === 'edit' && !isDirty()}>
                            {mode === 'edit' ? 'Spara ändringar' : 'Spara utkast'}
                        </ActionButton>
                    </div>
                </div>
            </form>

            {/* Unsaved-changes guard */}
            {showExitConfirm && (
                <div
                    className="absolute inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
                    onClick={() => setShowExitConfirm(false)}
                >
                    <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" /> Osparade ändringar
                        </h3>
                        <p className="text-sm text-gray-500 mt-1.5">Du har ändringar som inte sparats. Vill du spara dem?</p>
                        <div className="mt-4 space-y-2">
                            <ActionButton onClick={saveThenClose} loading={isLoadingNeeds} className="w-full">
                                {mode === 'edit' ? 'Spara ändringar' : 'Spara utkast'}
                            </ActionButton>
                            <div className="flex gap-2">
                                <button type="button" onClick={discardAndClose} className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
                                    Stäng utan att spara
                                </button>
                                <button type="button" onClick={() => setShowExitConfirm(false)} className="flex-1 px-3 py-2 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-50">
                                    Avbryt
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </IslandSlideOver>
    );
};

export default NeedFormModal;